import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../utils/supabase';
import { Text } from '../../common/Typography';
import { Card } from '../../common/Card';
import BlockBookingModal from '../BlockBookingModal';
import SessionTemplatesSection from '../SessionTemplatesSection';
import SessionSeriesSection from '../SessionSeriesSection';
import { inferBlockSeries } from '../../../utils/sessionSeries';
import {
  ManagementSection,
  ManagementActionRow,
} from '../../clubs/ManagementHubSection';
import { getSessionBlocks } from '../../../services/blockBookingService';
import BlockSessionsPanel from './BlockSessionsPanel';

/**
 * Configuration for block bookings and reusable templates.
 * Kept separate from the operational sessions hub (today / planned / past).
 */
const SessionsPlanningPanel = ({
  clubId,
  blockId,
  selectedClub,
  currentUser,
  onSessionsChange,
  onBlockLoaded,
  onBlockRemoved,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [blocks, setBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showBlockBookingModal, setShowBlockBookingModal] = useState(false);

  const fetchBlocks = async () => {
    if (!selectedClub?.id) return;
    setBlocksLoading(true);
    try {
      const result = await getSessionBlocks(selectedClub.id);
      if (result.success) setBlocks(result.data || []);
    } catch (err) {
      console.error('Error fetching blocks:', err);
    } finally {
      setBlocksLoading(false);
    }
  };

  const fetchTemplates = async () => {
    if (!selectedClub?.id) return;
    try {
      const { data, error } = await supabase
        .from('club_session_setup')
        .select('*')
        .eq('club_id', selectedClub.id)
        .eq('is_active', true)
        .order('session_name');

      if (!error && data) {
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  useEffect(() => {
    if (selectedClub?.id && !blockId) {
      fetchBlocks();
      fetchTemplates();
    }
  }, [selectedClub?.id, blockId]);

  const handleBlockBookingCreated = async (result) => {
    setShowBlockBookingModal(false);
    await fetchBlocks();
    onSessionsChange?.();
    if (result?.block?.id) {
      navigate(`/club/${clubId}/sessions/setup/${result.block.id}`, {
        state: location.state,
      });
    }
  };

  const openBlockSessions = (id) => {
    navigate(`/club/${clubId}/sessions/setup/${id}`, { state: location.state });
  };

  if (blockId) {
    return (
      <BlockSessionsPanel
        clubId={clubId}
        blockId={blockId}
        selectedClub={selectedClub}
        currentUser={currentUser}
        onBlockLoaded={onBlockLoaded}
        onBlockRemoved={onBlockRemoved}
      />
    );
  }

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        <Card variant="default" size="medium" className="!p-4 sm:!p-6">
          <ManagementSection iconType="sessions" title="Block bookings">
            {blocksLoading ? (
              <Text className="py-1 text-sm text-text-secondary">Loading…</Text>
            ) : (
              <>
                {blocks.map((block) => {
                  const sessionList = block.sessions || [];
                  const today = new Date().toISOString().split('T')[0];
                  const futureCount = sessionList.filter((s) => s.session_date >= today).length;
                  const totalCount = block.total_sessions ?? sessionList.length;
                  const series = inferBlockSeries(sessionList);
                  const descriptionParts = [
                    series?.mixed ? 'Mixed series' : series?.name || null,
                    `${totalCount} session${totalCount !== 1 ? 's' : ''}`,
                    futureCount > 0 ? `${futureCount} future` : null,
                    block.description || null,
                  ].filter(Boolean);

                  return (
                    <ManagementActionRow
                      key={block.id}
                      iconType="planning"
                      title={block.block_name}
                      description={descriptionParts.join(' · ')}
                      onClick={() => openBlockSessions(block.id)}
                    />
                  );
                })}
                <ManagementActionRow
                  iconType="planning"
                  title={blocks.length === 0 ? 'Create block booking' : 'Add block booking'}
                  onClick={() => setShowBlockBookingModal(true)}
                />
              </>
            )}
          </ManagementSection>
        </Card>

        <Card variant="default" size="medium" className="!p-4 sm:!p-6">
          <ManagementSection
            iconType="planning"
            title="Series"
            subtitle="Standing nights that can span many block bookings, e.g. Classic Tuesday."
          >
            <SessionSeriesSection selectedClub={selectedClub} />
          </ManagementSection>
        </Card>

        <Card variant="default" size="medium" className="!p-4 sm:!p-6">
          <ManagementSection iconType="planning" title="Templates">
            <SessionTemplatesSection
              selectedClub={selectedClub}
              currentUser={currentUser}
              embeddedInHub
            />
          </ManagementSection>
        </Card>
      </div>

      <BlockBookingModal
        isOpen={showBlockBookingModal}
        onClose={() => setShowBlockBookingModal(false)}
        clubId={clubId}
        templates={templates}
        onSuccess={handleBlockBookingCreated}
      />
    </>
  );
};

export default SessionsPlanningPanel;
